import { configureStore, createSelector, createSlice, current } from "@reduxjs/toolkit";
import { getCurrentProject } from "./sessionManager";
import { getAudioGraphInstance } from "./audioEngine";
import { sendProjectActionToServer } from "./serverComs";
import { midiHandler } from "./MidiHandler";
import { makeUID } from "./utils/math";
import { dx2x } from "./utils/array";
import { getInitialParamValues } from "./utils/parameters";
import { DelayReturn } from "./channels/DelayReturn";
import { ReverbReturn } from "./channels/ReverbReturn";
import { MasterChannel } from "./channels/MasterChannel";
import defaults from "./defaults.json";
import { factoryPresets } from "../assets/presets";

// transport controls playback, loop, record...
export const TRANSPORT_PARAMS_DESCRIPTION = {
    isRecordingProject: {type: 'bool', initial: false}, // this should be removed as it's derived from recordArmed and isPlaying.
    isRecordArmed: {type: 'bool', initial: false},
    isPlaying: {type: 'bool', initial: false},
    isMetronomeEnabled: { type: 'bool', initial: false },
    currentStep: {type: 'int', initial: -1},
    playStartMarkerStep: {type: 'int', initial: 0},
    playbackMode: {type: 'enum', options: ['followInstruments', 'followLauncher', 'followArrangement'], initial: 'followInstruments'},
    sync: {type: 'enum', options: ['send', 'receive', 'off'], initial: 'off'} // sync step with other projects
}

export const AUDIO_PARAMS_DESCRIPTION = {
    usesAudioEngine: { type: 'bool', initial: true }, // this should probably not be inside project
    isGraphBuilt: {type: 'bool', initial: false}, // should this be part of the store? we're not updating any UI with it. maybe we could combine both to have an audio status (will-start, starting, on, off). will-start so that with the first user-action the audiograph is built
}

export const PROJECT_PARAMS_DESCRIPTION = {
    bpm: {type: 'float', min: 40, max: 300, initial: 90},
    swing: {type: 'float', display: "percent", min: .5, max: .75, initial: .5, step: .01},
    compas: {type: 'enum', options: ['2/4', '3/4', '4/4'], initial: '4/4'},
    tonality: {
        type: 'enum',
        options:['c:major', 'c:minor',
            'c#:major', 'c#:minor',
            'd:major', 'd:minor',
            'eb:major', 'eb:minor',
            'e:major', 'e:minor',
            'f:major', 'f:minor',
            'f#:major', 'f#:minor',
            'g:major', 'g:minor',
            'ab:major', 'ab:minor',
            'a:major', 'a:minor',
            'bb:major', 'bb:minor',
            'b:major', 'b:minor'],
        initial: 'c:minor'
    }
}

const INITIAL_RETURNS_STATE = {
    "delayA": { parameters: { ...getInitialParamValues(DelayReturn.parametersDescription), feedback: 0 } },
    "delayB": { parameters: getInitialParamValues(DelayReturn.parametersDescription) },
    "reverbA": { parameters: { ...getInitialParamValues(ReverbReturn.parametersDescription), time: "1/8" } },
    "reverbB": { parameters: getInitialParamValues(ReverbReturn.parametersDescription) },
}

function dequeueSlot(state, action) {
    const {instrumentID} = action.payload;
    const launchQueue = state.transport.launchedSlots[instrumentID];
    if (launchQueue.nextScene !== undefined) {
        launchQueue.currentScene = launchQueue.nextScene;
        launchQueue.nextScene = undefined;
    }
}

function deleteAndReplacePreset(state, deletedPresetID, replacerPresetID) {
    Object.values(state.instruments).forEach(instrument => {
            if (instrument.parameters.presetID === deletedPresetID) {
            instrument.parameters.presetID = replacerPresetID;
        }
    });
    delete state.presets[deletedPresetID];
}

const projectSlice = createSlice({
    name: 'project',
    initialState: {
        metadata: Object.fromEntries(['id', 'name', 'connected_users'].map(key => [key, undefined])),
        scenes: { [makeUID("scene")]: { name: "Escena 1", slots: {}}, [makeUID("scene")]: { name: "Escena 2", slots: {}}, [makeUID("scene")]: { name: "Escena 3", slots: {}} ,},
        instruments: {},
        presets: Object.fromEntries(factoryPresets.map(preset => [makeUID("preset"), preset])),
        returns: INITIAL_RETURNS_STATE,
        masterChannel: { parameters: {...getInitialParamValues(MasterChannel.parametersDescription)}},
        parameters: Object.fromEntries([...Object.entries(PROJECT_PARAMS_DESCRIPTION)].map(([key, value]) => [key, value.initial])),
        transport: {...Object.fromEntries([...Object.entries(TRANSPORT_PARAMS_DESCRIPTION)].map(([key, value]) => [key, value.initial])), launchedSlots: {}},
        audio: Object.fromEntries([...Object.entries(AUDIO_PARAMS_DESCRIPTION)].map(([key, value]) => [key, value.initial])),
    },
    reducers: {
        loadAllData: (state, action) => {
            Object.assign(state, action.payload);
            Object.keys(action.payload.instruments).forEach(instrument => {
                state.transport.launchedSlots[instrument] ??= {currentScene: null, nextScene: undefined};
            })
        },
        updateMetadata: (state, action) => {
            state.metadata[action.payload.name] = action.payload.value;
        },
        setProjectParameter: (state, action) => {
            state.parameters[action.payload.name] = action.payload.value;
        },
        setTransportParameter: (state, action) => {
            const {name, value} = action.payload;
            if (name === "isPlaying" || name == "") throw new Error(`There are individual actions for setting transport/${name}`);
            state.transport[name] = value;
            if (name === "playbackMode") {

                if (value === "followArrangement") {
                    Object.values(state.transport.launchedSlots).forEach(launchQueue => {
                        launchQueue.nextScene = undefined;
                    })
                }
                else if (value === "followInstruments") {
                    Object.values(state.transport.launchedSlots).forEach(launchQueue => {
                        launchQueue.currentScene = null;
                        launchQueue.nextScene = undefined;
                    })
                }
            }
        },
        transportStart: (state, _action) => {
            state.transport.isPlaying = true;
        },
        transportStop: (state, _action) => {
            state.transport.isPlaying = false;
        },
        enqueueSlot: (state, action) => {
            const {sceneID, instrumentID} = action.payload;
            state.transport.launchedSlots[instrumentID].nextScene = sceneID;
            if (!state.transport.isPlaying) dequeueSlot(state, {payload: { instrumentID }});
        },
        launchScene: (state, action) => {
            const {sceneID} = action.payload;
            Object.keys(state.instruments).forEach(instrumentID => state.transport.launchedSlots[instrumentID].currentScene = sceneID);
        },
        dequeueSlots: (state, _action) => {
            Object.keys(state.instruments).forEach(instrumentID => dequeueSlot(state, {payload: { instrumentID }}));
        },
        setAudioParameter: (state, action) => {
            state.audio[action.payload.name] = action.payload.value;
            if (action.payload.name === "usesAudioEngine" && action.payload.value === false) {
                state.transport = {...state.transport,
                    isPlaying: false,
                    isRecordArmed: false,
                    isRecordingProject: false,
                }
            }
        },
        addInstrument: (state, action) => {
            const { id, data } = action.payload;
            state.instruments[id] = data;
            Object.values(state.scenes).forEach(scene => scene.slots[id] = {presetID: 'init'});
        },
        removeInstrument: (state, action) => {
            const { id } = action.payload;
            Object.values(state.scenes).forEach(scene => delete scene.slots[id]);
            delete state.instruments[id];
        },
        renameInstrument: (state, action) => {
            const { id, name } = action.payload;
            state.instruments[id].name = name;
        },
        reorderInstrument: (state, action) => {
            const { id, index } = action.payload;
            const array = Object.entries(state.instruments);
            const previousIndex = array.findIndex(([candidateId, _] ) => candidateId === id);
            const [instrument] = array.splice(previousIndex, 1);
            array.splice(index, 0, instrument);
            state.instruments = Object.fromEntries(array);
        },
        setInstrumentParameter: (state, action) => {
            // these parameters are the parameters untied to presets
            // ensureValidValue is missing right now
            const { instrumentID, parameter, value } = action.payload;
            state.instruments[instrumentID].parameters[parameter] = value;
        },
        makePreset: (state, action) => {
            const { presetID, data } = action.payload;
            state.presets[presetID] = data;
        },
        savePreset: (state, action) => {
            const { presetID, name, overwrittenID } = action.payload;
            state.presets[presetID]._metadata.name = name;
            delete state.presets[presetID]._metadata.isUnsaved;
            if (overwrittenID) deleteAndReplacePreset(state, overwrittenID, presetID);
        },
        deletePreset: (state, action) => {
            const { presetID } = action.payload;
            deleteAndReplacePreset(state, presetID, "init");
        },
        loadPresetInInstrument: (state, action) => {
            const { presetID, instrumentID } = action.payload;
            const previousPresetID = state.instruments[instrumentID].parameters.presetID;
            state.instruments[instrumentID].parameters.presetID = presetID;
            if (previousPresetID !== "init" && presetID !== previousPresetID && state.presets[previousPresetID]._metadata.isUnsaved) {
                delete state.presets[previousPresetID];
            }
        },
        setReturnParameter: (state, action) => {
            const { returnName, parameter, value } = action.payload;
            state.returns[returnName].parameters[parameter] = value;
        },
        setMasterChannelParameter: (state, action) => {
            const { name, value } = action.payload;
            state.masterChannel.parameters[name] = value;
        },
        setPresetParameter: (state, action) => {
            const { presetID, parameter, value } = action.payload;
            if (!(presetID in state.presets)) {
                console.warn(`Preset with id ${presetID} could not be found in the store. Maybe there's a makePreset action that still didn't arrive?`);
                return;
            };

            state.presets[presetID][parameter] = value;
        },
        setClip: (state, action) => {
            const { instrumentID, clipNumber, value } = action.payload;
            state.instruments[instrumentID].clips[clipNumber] = value;
        },
        setClipParameter: (state, action) => {
            const { instrumentID, parameter, value } = action.payload;
            const clipNumber = action.payload.clipNumber ?? state.instruments[instrumentID].parameters.clipNumber;
            state.instruments[instrumentID].clips[clipNumber].sound[parameter] = value;
        },
        clearClip: (state, action) => {
            const { instrumentID, clipNumber } = action.payload;
            const clip = state.instruments[instrumentID].clips[clipNumber];
            if (clip.sound) clip.sound = {}
            if (clip.sequence) clip.sequence = []
        },
        setClipSequence: (state, action) => {
            const { instrumentID, clipNumber, value } = action.payload;
            state.instruments[instrumentID].clips[clipNumber].sequence = value;
        },
        setClipLength: (state, action) => {
            const { instrumentID, clipNumber, value } = action.payload;
            state.instruments[instrumentID].clips[clipNumber].length = value; 
        },
        addScene: (state, action) => {
            const { id, name } = action.payload;
            state.scenes[id] = { slots: Object.fromEntries([...Object.keys(state.instruments)].map(id => [id, {presetID: 'init'}])), name };
        },
        removeScene: (state, action) => {
            const { id } = action.payload;
            delete state.scenes[id];
        },
        renameScene: (state, action) => {
            const { id, name } = action.payload;
            state.scenes[id].name = name;
        },
        reorderScene: (state, action) => {
            const { id, index } = action.payload;
            const array = Object.entries(state.scenes);
            const previousIndex = array.findIndex(([candidateId, _] ) => candidateId === id);
            const [scene] = array.splice(previousIndex, 1);
            array.splice(index, 0, scene);
            state.scenes = Object.fromEntries(array);
        },
        setSceneDuration: (state, action) => {
            const { id, duration } = action.payload;
            state.scenes[id].duration = duration;
        },
        setClipNumberForInstrumentInScene: (state, action) => {
            const { instrumentID, sceneID, clipNumber } = action.payload;
            state.scenes[sceneID].slots[instrumentID].clipNumber = clipNumber;
        },
        removeClipForInstrumentInScene: (state, action) => {
            const { instrumentID, sceneID } = action.payload;
            delete state.scenes[sceneID].slots[instrumentID].clipNumber;
        },
        setPresetIDForInstrumentInScene: (state, action) => {
            const { instrumentID, sceneID, presetID } = action.payload;
            state.scenes[sceneID].slots[instrumentID].presetID = presetID;
        },
    }
})

function updateAudioGraphForInstrumentsWithNewPreset(previousState, newPreset) {
    Object.values(getCurrentProject().instruments).forEach(instrument => {
        if (
            newPreset === instrument.getPlaybackPresetID()
            && newPreset !== instrument.getPlaybackPresetID(previousState)
        ) instrument.updateAudioGraphForPlaybackPreset();
    });
}
// Triguejem canvis a l'audio graph després d'actualitzar l'store
const audioApplier = storeAPI => next => action => {
  const previousState = storeAPI.getState();
  let result = next(action); // primer actualitzem l'store
  const currentState = storeAPI.getState();
  if (getCurrentProject() == null) return result;
  switch (action.type) {
    case "project/loadAllData":
        Object.entries(action.payload.instruments).forEach(([instrumentID, instrumentData]) => getAudioGraphInstance().addInstrument(instrumentID, instrumentData));
        break;
    case "project/setInstrumentParameter":
        const {instrumentID, parameter, value} = action.payload;
        if (parameter === "isSolo" || parameter === "isMute") {
            Object.values(getCurrentProject().instruments).forEach(instrument => instrument.updateMute());
            break;
        }
        const instrument =  getCurrentProject().getInstrument(instrumentID);
        instrument.applyAudioGraphParameter(parameter, value);
        break;
    case "project/savePreset": {
        const { presetID } = action.payload;
        updateAudioGraphForInstrumentsWithNewPreset(previousState, presetID);
        break;
    };
    case "project/deletePreset": {
        updateAudioGraphForInstrumentsWithNewPreset(previousState, "init");
        break;
    }
    case "project/loadPresetInInstrument": {
        const { presetID, instrumentID } = action.payload;
        const instrument = getCurrentProject().getInstrument(instrumentID);
        if (presetID === instrument.getPlaybackPresetID()) instrument.updateAudioGraphForPlaybackPreset();
        break;
    }
    case "project/setReturnParameter": {
        const {returnName, parameter, value} = action.payload;
        const returnNode = getAudioGraphInstance().returns[returnName];
        returnNode[parameter] = value;
        break;
    }
    case "project/setMasterChannelParameter": {
        const { name, value } = action.payload;
        getAudioGraphInstance().masterChannel[name] = value;
        break;
    }
    case "project/setPresetParameter": {
        const { presetID, parameter, value } = action.payload;
        if (!(presetID in selectPresets(currentState))) break;
        const instrumentType = selectPresets(currentState)[presetID]._metadata.instrumentType;
        getCurrentProject().getInstrumentsOfType(instrumentType).forEach(instrument => {
            if (presetID === instrument.getPlaybackPresetID()){
                instrument.applyAudioGraphParameter(parameter, value);
            }
        })
        break;
    }
    case "project/setClip": {
        const { instrumentID } = action.payload;
        // would be more optimized if it only did it when the clip corresponded to the playback clip
        // also only for audio clips (the ones used in looper), not midi clips, as those don't have any audiograph parameters.
        getCurrentProject().getInstrument(instrumentID).updateAudioGraphForPlaybackClip();
        break;
    }
    case "project/setClipParameter": {
        const { instrumentID, parameter, value } = action.payload;
        getCurrentProject().getInstrument(instrumentID).applyAudioGraphParameter(parameter, value);
        break;
    }
    case "project/setProjectParameter": {
        const { name, value } = action.payload;
        switch (name) {
        case 'bpm':
            getAudioGraphInstance().applyBpm(value);
            break;
        }
        break;
    }
    case "project/setAudioParameter": {
        const { name, value } = action.payload;
        switch (name) {
            case "usesAudioEngine":
                if (value) getAudioGraphInstance().turnAudioOn(); //must be initialized from user action so can't go here
                else getAudioGraphInstance().turnAudioOff();
        }
        break;
    }
    case "project/addInstrument":
        getCurrentProject().afegeixInstrument(action.payload.id, action.payload.data); // todo: remove this. instrument objects should go in audioinstance
        getAudioGraphInstance().addInstrument(action.payload.id, action.payload.data);
        break;
    case "project/removeInstrument":
        getAudioGraphInstance().removeInstrument(action.payload.id);
        getCurrentProject().eliminaInstrument(action.payload.id);  // todo: remove this. instrument objects should go in audioinstance
        break;
    case "project/setTransportParameter":
        const {name} = action.payload;
        if (name === "currentStep") {
            // this should actually go in server communications middleware.
            // also maybe isTransposrtSyncMaster could come from state directly
            if (getAudioGraphInstance().isTransportSyncMaster()) {
                sendMessageToServer('update_master_sequencer_current_step', {current_step: currentStep});
            }
        } else if (name === "playbackMode") Object.values(getCurrentProject().instruments).forEach(instrument => instrument.applyStateToAudioGraph());
        break;
    case "project/transportStart":
        getAudioGraphInstance().transportStart();
        break;
    case "project/transportStop":
        getAudioGraphInstance().transportStop();
        break;
    case "project/setClipNumberForInstrumentInScene" : {
        const { instrumentID } = action.payload;
        if (selectPlaybackMode(currentState) === "followLauncher") getCurrentProject().getInstrument(instrumentID).updateAudioGraphForPlaybackClip();
        break;
    }
    case "project/setPresetIDForInstrumentInScene": {
        const { instrumentID } = action.payload;
        if (selectPlaybackMode(currentState) === "followLauncher") getCurrentProject().getInstrument(instrumentID).updateAudioGraphForPlaybackPreset();
        break;
    }
    case "project/launchScene": {
        // todo: només s'ha d'actualitzar pels presets que han canviat!
        Object.values(getCurrentProject().instruments).forEach(instrument => instrument.updateAudioGraphForPlaybackPreset());
        // todo: només s'ha d'actualitzar pels clips que han canviat!
        Object.values(getCurrentProject().instruments).forEach(instrument => instrument.updateAudioGraphForPlaybackPreset());
        break;
    }
    case "project/dequeueSlots": {
        // todo: només s'ha d'actualitzar pels presets que han canviat!
        Object.values(getCurrentProject().instruments).forEach(instrument => instrument.updateAudioGraphForPlaybackPreset());
        // todo: només s'ha d'actualitzar pels clips que han canviat!
        Object.values(getCurrentProject().instruments).forEach(instrument => instrument.updateAudioGraphForPlaybackPreset());
        break;
    }
  }
  return result;
}

const serverSenderMiddleware = _storeAPI => next => action => {
    let result = next(action);
    if (getCurrentProject() == null) return result;
    if (action.type.startsWith("project")
        && action.type !== ("project/loadAllData") // don't want to trigger that here
        && action.type !== ("project/setTransportParameter") // all of these are local. perhaps it' be better to specify it in the payload
        && action.type !== ("project/transportStart") // local
        && action.type !== ("project/transportStop") // local
        && action.type !== ("project/enqueueSlot") // local
        && action.type !== ("project/launchScene") // local
        && action.type !== ("project/dequeueSlots") // local
        && action.type !== ("project/setAudioParameter") // all of these are local. perhaps it' be better to specify it in the payload
        && !action.payload.isLocal) {
        sendProjectActionToServer(action);
    }
  return result;
}

// local settings
const settingsSlice = createSlice({
    name: 'settings',
    initialState: {
        sampleRate: 27500,
        midiDevice: null,
    },
    reducers: {
        setSampleRate: (state, action) => {
            const value = action.payload;
            state.sampleRate = value;
        },
        setMidiDevice: (state, action) => {
            const value = action.payload;
            state.midiDevice = value;
        }
    }
});

const settingsMiddleware = _storeAPI => next => action => {
    let result = next(action);
    if (action.type === "settings/setMidiDevice") {
        midiHandler.externalDeviceManager.bindToInput(action.payload);
    }
    return result;
}

// persistence (localstorage)
// from https://read.reduxbook.com/markdown/part2/12-clientside-persistance
// it could be a bit smarter about how it does stuff
const persistMiddleware = storeAPI => next => action => {
    const result = next(action);
    const shouldPersist = action.type.startsWith("settings");
    if (shouldPersist) {
        const appState = storeAPI.getState();
        localStorage.setItem('settings', JSON.stringify(appState.settings));
    }
    return result;
}

const persistedSettings = JSON.parse(localStorage.getItem('settings')) ?? undefined;

export default configureStore({
    reducer: {
        project: projectSlice.reducer,
        settings: settingsSlice.reducer,
    },
    middleware: (getDefaultMiddleware) => getDefaultMiddleware()
        .concat(audioApplier)
        .concat(serverSenderMiddleware)
        .concat(settingsMiddleware)
        .concat(persistMiddleware),
    preloadedState: {settings: {...settingsSlice.getInitialState(), ...persistedSettings}},
})

export const {
    loadAllData,
    updateMetadata,
    setProjectParameter,
    setTransportParameter,
    transportStart,
    transportStop,
    enqueueSlot,
    launchScene,
    dequeueSlots,
    setAudioParameter,
    addInstrument,
    removeInstrument,
    reorderInstrument,
    renameInstrument,
    setInstrumentParameter,
    makePreset,
    savePreset,
    deletePreset,
    loadPresetInInstrument,
    setReturnParameter,
    setMasterChannelParameter,
    setPresetParameter,
    setClip,
    setClipParameter,
    clearClip,
    setClipSequence,
    setClipLength,
    addScene,
    removeScene,
    reorderScene,
    renameScene,
    setSceneDuration,
    setClipNumberForInstrumentInScene,
    removeClipForInstrumentInScene,
    setPresetIDForInstrumentInScene,
} = projectSlice.actions

export const selectProject = state => state.project;

export const selectProjectID = state => state.project.metadata.id;
export const selectProjectName = state => state.project.metadata.name;
export const selectConnectedUsers = state => state.project.metadata.connected_users;

export const selectScenes = state => state.project.scenes;

export const selectArrangement = createSelector(selectScenes, scenes => {
    let nextOnset = 0;
    return Object.entries(scenes).map(([sceneID, {duration, name}]) => {
        const onset = nextOnset;
        const length = (duration ?? 2) * 16;
        nextOnset += length;
        return { sceneID, name, length, onset }
    })
});

export const selectBpm = state => state.project.parameters.bpm;
export const selectCompas = state => state.project.parameters.compas;
export const selectSwing = state => state.project.parameters.swing;
export const selectTonality = state => state.project.parameters.tonality;

export const selectInstruments = state => state.project.instruments;
export const selectInstrumentIDs = createSelector(selectInstruments, instruments => Object.keys(instruments));
export const selectIsAnyInstrumentSolo = state => [...Object.values(selectInstruments(state))].some(data => data.parameters.isSolo);

export const selectPresets = state => state.project.presets;

// transport
export const selectIsRecordingProject = state => state.project.transport.isRecordingProject;
export const selectIsRecordArmed = state => state.project.transport.isRecordArmed;
export const selectIsPlaying = state => state.project.transport.isPlaying;
export const selectIsMetronomeEnabled = state => state.project.transport.isMetronomeEnabled;
export const selectTransportCurrentStep = state => state.project.transport.currentStep;
export const selectPlayStartMarkerStep = state => state.project.transport.playStartMarkerStep;
export const selectTransportSync = state => state.project.transport.sync;
export const selectPlaybackMode = state => state.project.transport.playbackMode;
export const selectlaunchedSlots = state => state.project.transport.launchedSlots;

export const selectCurrentSceneIDAndStep = createSelector(selectPlaybackMode, selectTransportCurrentStep, selectScenes, (playbackMode, currentStep, scenes) => {
        const startingBars = dx2x([...Object.values(scenes)].map(({duration=defaults.SCENE_DURATION}) => duration)); // the last items is the ending measure
        if (playbackMode !== "followArrangement") return {step: currentStep};
        const currentBar = Math.floor(currentStep / 16);
        let currentSceneIndex = 0;
        while (currentSceneIndex+1 < startingBars.length) {
            if (startingBars[currentSceneIndex+1] > currentBar) break;
            currentSceneIndex++;
        }
        if (currentSceneIndex === startingBars.length - 1) return {}; // finished
        const currentSceneBar = currentBar - startingBars[currentSceneIndex];
        const step = currentStep - (currentBar - currentSceneBar) * 16;
        const sceneID = Object.keys(scenes)[currentSceneIndex];
        return {sceneID, step};
    });
export const createMasterChannelParameterSelector = parameterName => state => state.project.masterChannel.parameters[parameterName];

// audio
export const selectUsesAudioEngine = state => state.project.audio.usesAudioEngine;
export const selectIsGraphBuilt = state => state.project.audio.isGraphBuilt;

export const selectReturns = state => state.project.returns;
export const createReturnParameterSelector = (returnName, parameterName) => state => state.project.returns[returnName].parameters[parameterName];

PROJECT_PARAMS_DESCRIPTION.swing.selector = selectSwing;
PROJECT_PARAMS_DESCRIPTION.swing.updater = value => setProjectParameter({name: "swing", value})
PROJECT_PARAMS_DESCRIPTION.bpm.selector = selectBpm;
PROJECT_PARAMS_DESCRIPTION.bpm.updater = value => setProjectParameter({name: "bpm", value})

export const {
    setSampleRate,
    setMidiDevice,
} = settingsSlice.actions;

// settings
export const selectSampleRate = state => state.settings.sampleRate;
export const selectMidiDevice = state => state.settings.midiDevice;

// https://redux-remember.js.org/quick-start/